import { Timestamp } from '@ms/yammer-protobuf';

export interface BaseTelemetryEvent {
  readonly type: TelemetryEventType;
  readonly name: string;
  readonly occurredAt: string;
  readonly skipAuthentication?: boolean;
}

export enum TelemetryEventType {
  Analytics = 'Analytics',
  AnalyticsV2 = 'AnalyticsV2',
  Error = 'Error',
  Info = 'Info',
  Performance = 'Performance',
}

export interface PropertyCheckEventProperties {
  readonly droppedKeys: string;
}

export type TelemetryPropertyValueType =
  | string
  | number
  | boolean
  | undefined
  | null
  | string[]
  | AnalyticsV2EventTimestamp
  | readonly PerformanceServerTiming[];

export type AnalyticsV2EventTimestamp =
  | {
      readonly seconds: string | number | null;
      readonly nanos: string | number | null;
    }
  | Timestamp;

export type TelemetryClientId =
  | 'UNKNOWN'
  | 'YAMMER_EMBED'
  | 'YAMMER_VIVA_CONNECTIONS'
  | 'YAMMER_METAOS_OUTLOOK_PERSONAL_APP'
  | 'YAMMER_METAOS_OFFICE_PERSONAL_APP'
  | 'YAMMER_OUTLOOK'
  | 'YAMMER_SHAREPOINT'
  | 'YAMMER_SHAREPOINT_SHARETO'
  | 'YAMMER_TEAMS'
  | 'YAMMER_TEAMS_MEETING'
  | 'YAMMER_TEAMS_NATIVE_APP'
  | 'YAMMER_TEAMS_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_OUTLOOK_PERSONAL_APP'
  | 'YAMMER_VIVAENGAGE_OFFICE_PERSONAL_APP'
  | 'YAMMER_WEB';

export interface ClientSuppliedConfig {
  readonly yammerAppClientId: string;
  readonly telemetryClientId: TelemetryClientId;
  readonly gitRevision: string;
  readonly batchSizeInBytes: number;
  readonly eventReportIntervalInMilliseconds: number;
  readonly reportAuthorizationReceiver: string;
  readonly reportAuthorizationFallbacks: string[];
}

export interface ClientConfig extends ClientSuppliedConfig {
  readonly appInstanceId: string;
  readonly loadedAt: number;
  readonly resource: string;
  readonly graphqlHost: string;
  readonly telemetryHost: string;
  readonly skipResourceTimingReportForExternalJavascript: boolean;
  readonly shouldLogProtobufEventsToConsole: boolean;
}

export type HostingEnvironment =
  | 'dev'
  | 'edog'
  | 'MSIT'
  | 'prodbubble'
  | 'PROD'
  | 'SDFV2'
  | 'Dogfood'
  | 'dogfood'
  | 'Microsoft'
  | 'FirstRelease'
  | 'WW';

export interface HostingConfig extends Record<string, unknown> {
  /**
   * A specific partition of substrate servers in a particular region
   */
  readonly forest?: string;
  /**
   * The environment from which the assets are being served. This
   * could be the deployment ring that the user is a part of or the local
   * development environment (e.g. prod, sdfv2, dev)
   */
  readonly hostingEnvironment: HostingEnvironment;
  /**
   * The host from which the app is loaded.
   */
  readonly hostname?: string;
  /**
   * The path to the CDN hosting the script assets
   */
  readonly scriptBaseUrl?: string;
  /**
   * revision: the current build version. Format: $(date:yyyyMMdd)$(rev:rrr)
   */
  readonly scriptRevision: string;
  /**
   * The specific name of the server that served the html page
   */
  readonly server?: string;
  /**
   * The version of the server
   */
  readonly serverPageVer?: string;
  /**
   * The path to the static assets for loading the page
   * (e.g. service worker, favicon)
   */
  readonly staticBaseUrl?: string;
  /**
   * A unique value for tracking a user across the same browser across time
   */
  readonly userBrowserIdentity?: string;
  /**
   * A unique value for yammer-sharepoint webpart to track it across sharepoint and yammer
   */
  readonly webPartInstanceId?: string;
  /**
   * A unique value for tracking yammer-sharepoint webpart request to sharepoint server
   *
   */
  readonly webPartCorrelationId?: string;
}

export interface ClientContext {
  readonly osName?: string;
  readonly deviceType?: string;
  readonly hostClientType?: string;
  readonly clientFeedLayout?: string;
  readonly hostAppName?: string;
}

export interface FirstMeaningfulPaintContext {
  readonly firstMeaningfulPaintReported: boolean;
  readonly authPageLoadDuration?: number;
}

export interface PerformanceMeasure {
  readonly eventName: PerformanceEventName;
  readonly measureKey: string;
}

export enum PerformanceEventName {
  AcquireTokenForResource = 'acquire_token_for_resource',
  ApiFetch = 'api_fetch',
  AzureVideoLoad = 'azure_video_load',
  AuthBootstrap = 'auth_bootstrap',
  AuthPageLoad = 'auth_page_load',
  Bootstrap = 'bootstrap',
  BootstrapBundleLoad = 'bootstrap_bundle_load',
  BootstrapNavigation = 'bootstrap_navigation',
  CumulativeLayoutShift = 'cumulative_layout_shift',
  FileAttach = 'file_attach',
  FileBrowserLoad = 'file_browser_load',
  FilePickerLoad = 'file_picker_load',
  FileUpload = 'file_upload',
  FirstContentfulPaint = 'first_contentful_paint',
  FirstInputDelay = 'first_input_delay',
  FirstMeaningfulPaint = 'first_meaningful_paint',
  FirstPaint = 'first_paint',
  ImageLoad = 'image_load',
  LargestContentfulPaint = 'largest_contentful_paint',
  LoadTeamsMeetingDetails = 'load_teams_meeting_details',
  ManagedApiAction = 'managed_api_action',
  MessagePost = 'message_post',
  MomentHomeCarouselLoad = 'moment_home_carousel_load',
  MomentsNavigation = 'moments_navigation',
  MomentUserCarouselLoad = 'moment_user_carousel_load',
  PerceivedImageLoad = 'perceived_image_load',
  RendererBundleLoad = 'renderer_bundle_load',
  ResourceTiming = 'resource_timing',
  ServiceWorkerRegistration = 'service_worker_registration',
  ServiceWorkerDisable = 'service_worker_disable',
  SharePointAuthTokenFetch = 'sharepoint_auth_token_fetch',
  SharePointWebPartConfiguration = 'webpart_config',
  TeamsLiveEventVideoLoad = 'teams_liveevent_video_load',
  TimeToFirstByte = 'time_to_first_byte',
  UiComponentMeasure = 'ui_component_measure',
}

export interface ScenarioPerformanceData {
  readonly timer: number;
  readonly measure: PerformanceMeasure;
}

export type ScenarioCollection = Record<string, ScenarioPerformanceData>;
